import React, { useState, useEffect } from 'react';
import useTheme from '@material-ui/core/styles/useTheme';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import getUsages from '@afosto/utils-react/getUsages';
import TinyLineChart from '../../../components/TinyLineChart';
import arrayFromNumber from '@afosto/utils/array/arrayFromNumber';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import translations from './translations';
import Skeleton from '@material-ui/lab/Skeleton';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';

const renderTransformationsTooltip = args => {
  const { payload } = args;
  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="textSecondary">
          {payload?.[0]?.payload?.date && (
            <FormattedDate
              value={new Date(payload[0].payload.date)}
              weekday="long"
              day="numeric"
              month="long"
            />
          )}
        </Typography>
        <Typography variant="body1">
          <FormattedNumber value={payload?.[0]?.payload?.quantity ?? 0} />
        </Typography>
      </CardContent>
    </Card>
  );
};

const TransformationsThisMonthSection = () => {
  const [transformationsThisMonth, setTransformationsThisMonth] = useState(null);
  const [transformationsThisMonthPerDay, setTransformationsThisMonthPerDay] = useState(null);
  const [isLoadingMonth, setIsLoadingMonth] = useState(true);
  const [isLoadingPerDay, setIsLoadingPerDay] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    let isMounted = true;

    const getTransformationsThisMonth = async () => {
      try {
        const startOfTheMonth = new Date();
        startOfTheMonth.setDate(1);

        const currentDate = new Date();

        const data = await getUsages({
          unit: 'miss',
          code: 'qcq',
          startAt: startOfTheMonth,
          endAt: currentDate,
          interval: '1m',
          as: 'sum',
        });

        if (isMounted) {
          setTransformationsThisMonth(data);
        }
      } catch (error) {
        if (isMounted) {
          setTransformationsThisMonth(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingMonth(false);
        }
      }
    };

    const getTransformationsThisMonthPerDay = async () => {
      try {
        const startOfTheMonth = new Date();
        startOfTheMonth.setDate(1);

        const currentDate = new Date();

        const data = await getUsages({
          unit: 'miss',
          code: 'qcq',
          startAt: startOfTheMonth,
          endAt: currentDate,
          interval: '1d',
          as: 'sum',
        });

        const daysInMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          0,
        ).getDate();

        const extraDays = arrayFromNumber(daysInMonth)
          .slice(currentDate.getDate(), daysInMonth)
          .map(num => ({
            date: new Date(currentDate.getFullYear(), currentDate.getMonth(), num + 1),
          }));

        const formattedData = [
          ...(data?.buckets?.length
            ? data.buckets.reduce(
                (acc, bucket) => [
                  ...acc,
                  {
                    ...bucket,
                    date: new Date(bucket.date),
                  },
                ],
                [],
              )
            : []),
          ...extraDays,
        ];

        if (isMounted) {
          setTransformationsThisMonthPerDay({ ...data, buckets: formattedData });
        }
      } catch (error) {
        if (isMounted) {
          setTransformationsThisMonthPerDay(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPerDay(false);
        }
      }
    };

    getTransformationsThisMonth();
    getTransformationsThisMonthPerDay();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoadingMonth) {
    return (
      <Card>
        <CardContent>
          <Typography variant="overline">
            <FormattedMessage {...translations.title} />
          </Typography>
          <Typography variant="h5" gutterBottom>
            <Skeleton variant="typograohy" width={120} />
          </Typography>
          <LoadingWrapper isLoading={isLoadingPerDay}>
            <TinyLineChart
              data={transformationsThisMonthPerDay?.buckets ?? []}
              lines={[
                {
                  dataKey: 'quantity',
                  type: 'linear',
                  stroke: theme.palette.primary.main,
                  strokeWidth: 2,
                },
              ]}
              xAxisKey="date"
              renderTooltip={renderTransformationsTooltip}
            />
          </LoadingWrapper>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="overline">
          <FormattedMessage {...translations.title} />
        </Typography>
        <Typography variant="h5" gutterBottom>
          {transformationsThisMonth?.buckets ? (
            <FormattedNumber value={transformationsThisMonth?.buckets?.[0]?.quantity} />
          ) : (
            '-'
          )}
        </Typography>
        <LoadingWrapper isLoading={isLoadingPerDay}>
          <TinyLineChart
            data={transformationsThisMonthPerDay?.buckets}
            lines={[
              {
                dataKey: 'quantity',
                type: 'linear',
                stroke: theme.palette.primary.main,
                strokeWidth: 2,
              },
            ]}
            xAxisKey="date"
            renderTooltip={renderTransformationsTooltip}
          />
        </LoadingWrapper>
      </CardContent>
    </Card>
  );
};

export default TransformationsThisMonthSection;
